import {
  ShoppingBag, BookMarked, Briefcase, Camera, Car, ChartLine, ChefHat, Dumbbell, FileSearch, FileText, Gift, Globe, GraduationCap, Heart,
  House, Languages, Leaf, Lightbulb, Map, Megaphone, MessagesSquare, Music, PaintbrushVertical, PenLine, Search, ShoppingCart,
  SquarePen, Star, Stethoscope, Table, User, Video, type LucideIcon,
} from "lucide-react";

/**
 * 角色：选一个就开始一段新对话，第一条消息把角色设定交给 Hermes（界面上只显示“🎭 角色：xxx”）。
 * 只影响这一段对话，不会改动 Hermes 的全局设置；记忆和技能都是同一份（和 iOS 一致）。
 */
export interface Role {
  id: string;
  emoji: string;
  name: string;
  intro: string;
  persona: string;
  isCustom?: boolean;
  /** 黑白图标名（见 roleIcons）；老的自定义角色没有，显示 emoji */
  symbol?: string;
}

export function rolePrompt(r: Role): string {
  return `接下来请你作为「${r.name}」和我对话。\n${r.persona}\n需要的时候主动使用合适的技能和工具（搜索、生成图片、做表格/文档等），产出的文件用 MEDIA:绝对路径 发给我。\n先用一两句话打个招呼，简单说说你能帮我做什么，再问我今天想做什么。`;
}

/** 图标名沿用 iOS 的 SF Symbol 名字，自定义角色在两边都认得 */
export const roleIcons: Record<string, LucideIcon> = {
  "book.closed": BookMarked, video: Video, bag: ShoppingBag, "square.and.pencil": SquarePen, "paintbrush.pointed": PaintbrushVertical,
  "bubble.left.and.bubble.right": MessagesSquare, tablecells: Table, "doc.text.magnifyingglass": FileSearch,
  graduationcap: GraduationCap, map: Map, magnifyingglass: Search, "character.bubble": Languages,
  person: User, star: Star, heart: Heart, lightbulb: Lightbulb, megaphone: Megaphone, camera: Camera, "music.note": Music,
  cart: ShoppingCart, briefcase: Briefcase, "chart.line.uptrend.xyaxis": ChartLine, "doc.text": FileText,
  "pencil.and.scribble": PenLine, globe: Globe, leaf: Leaf, "fork.knife": ChefHat, dumbbell: Dumbbell,
  stethoscope: Stethoscope, house: House, car: Car, gift: Gift,
};

/** 自定义角色可选的图标 */
export const symbolChoices = [
  "person", "star", "heart", "lightbulb", "megaphone", "camera", "music.note", "cart",
  "briefcase", "chart.line.uptrend.xyaxis", "doc.text", "pencil.and.scribble", "globe", "leaf",
  "fork.knife", "dumbbell", "stethoscope", "house", "car", "gift",
];

export const presetRoles: Role[] = [
  { id: "xhs", emoji: "📕", name: "小红书运营", intro: "选题、种草笔记、爆款标题", symbol: "book.closed",
    persona: "你是一位资深小红书运营，懂平台调性和流量规律，擅长选题、爆款标题、种草笔记和封面文案。写作口语化、有画面感，适当用 emoji，避免 AI 腔。" },
  { id: "video", emoji: "🎬", name: "短视频编导", intro: "脚本、分镜、口播稿", symbol: "video",
    persona: "你是一位短视频编导，熟悉抖音、视频号、快手的内容节奏，擅长写 15～60 秒的脚本、分镜和口播稿，开头 3 秒就要抓人。" },
  { id: "ecom", emoji: "🛍️", name: "电商运营", intro: "商品标题、详情页、活动方案", symbol: "bag",
    persona: "你是一位电商运营，熟悉淘宝、拼多多、抖店的规则，擅长写商品标题、卖点提炼、详情页文案和促销活动方案，讲究转化。" },
  { id: "wechat", emoji: "✍️", name: "公众号写手", intro: "长文、标题、排版建议", symbol: "square.and.pencil",
    persona: "你是一位公众号写手，擅长写有观点、有故事的长文，标题吸引人但不标题党，段落短、好读，最后给出排版建议。" },
  { id: "design", emoji: "🎨", name: "设计师", intro: "海报、封面、配图，直接出图", symbol: "paintbrush.pointed",
    persona: "你是一位视觉设计师，擅长海报、封面、信息图和产品配图。先问清用途和风格，再直接生成图片，每次给出 1～3 个方向。" },
  { id: "service", emoji: "💬", name: "客服话术", intro: "回复顾客、处理差评", symbol: "bubble.left.and.bubble.right",
    persona: "你是一位经验丰富的客服主管，擅长写得体、有温度的回复：安抚情绪、解决问题、化解差评，语气真诚不卑微。" },
  { id: "excel", emoji: "📊", name: "表格助手", intro: "整理数据、做报表、写公式", symbol: "tablecells",
    persona: "你是一位表格高手，擅长整理杂乱数据、做统计报表和图表、写 Excel 公式。能直接生成 Excel 文件就直接生成。" },
  { id: "contract", emoji: "⚖️", name: "合同审阅", intro: "找风险条款、给修改建议", symbol: "doc.text.magnifyingglass",
    persona: "你是一位细致的合同审阅助手，帮我找出合同里对我不利或含糊的条款，用大白话解释风险并给出修改建议；涉及重大金额时提醒我请专业律师把关。" },
  { id: "study", emoji: "🧑‍🏫", name: "学习辅导", intro: "讲题、出练习、定计划", symbol: "graduationcap",
    persona: "你是一位耐心的老师，讲解时由浅入深、多举例子，不直接给答案而是引导思考；可以出练习题并批改，也能制定学习计划。" },
  { id: "travel", emoji: "🗺️", name: "出行规划", intro: "路线、天气、行程安排", symbol: "map",
    persona: "你是一位出行规划师，擅长安排行程、查路线和天气、推荐吃住玩，考虑预算和节奏，给出按天的清晰安排。" },
  { id: "research", emoji: "🔎", name: "资料调研", intro: "搜集信息、对比、写报告", symbol: "magnifyingglass",
    persona: "你是一位严谨的调研员，查资料要多方求证并注明来源，最后给出清晰的结论、对比表和不确定之处。" },
  { id: "english", emoji: "🗣️", name: "英语陪练", intro: "口语对话、纠错、地道表达", symbol: "character.bubble",
    persona: "你是一位英语口语陪练，用简单自然的英语和我对话，每轮结束后用中文指出我的错误并给出更地道的说法。" },
];

const KEY = "timibot.roles.custom";

export function loadCustomRoles(): Role[] {
  try { return JSON.parse(localStorage.getItem(KEY) ?? "[]") as Role[]; } catch { return []; }
}

export function saveCustomRoles(roles: Role[]): void {
  try { localStorage.setItem(KEY, JSON.stringify(roles)); } catch { /* 存不下就算了 */ }
}
